/**
 * The Matchup tab: the read-only `Betting line` block, then
 * /event/detail-get's sections in server order, dispatched by kind (ISSUE-988,
 * shared with ISSUE-989). Stacked below lg; two columns at lg when isWide.
 *
 * The Betting line block shows one summary line (favorite and spread, then
 * the total) built from `odds` by ISSUE-985's buildLineSummary; the full
 * markets live on the Odds tab. `odds` is read defensively: ISSUE-169's field
 * on `/event/detail-get` is optional, so a missing field or an event with no
 * line both resolve to `null`, the "No line yet" state SUR-61/Q136 asks for.
 * `odds` is the one line source: ISSUE-1060 dropped ISSUE-988's duplicate
 * `matchup.lineSummary` from the wire, so the fan sees one line, not two.
 *
 * ISSUE-989 adds the line score and scoring summary kinds, the player link on
 * game leaders, and the final result line under the Betting line.
 *
 * ISSUE-1091. While a game is live the tab opens on the server's live state
 * (`LiveStatePanel`), above the Betting line: the larger diamond with the count
 * and outs, or the field strip with the ball spot.
 *
 * ISSUE-1382. Under it sits the Last play card, the server's newest play.
 */
import type { ReactNode } from "react";

import { LiveStatePanel } from "../liveState/LiveStatePanel";
import type { typeLiveStateWire } from "../liveState/liveStateTypes";
import { DetailsSection } from "./DetailsSection";
import { GameSectionCard, GameSectionEmpty } from "./GameSectionCard";
import { HeadToHeadSection } from "./HeadToHeadSection";
import { LastPlayCard, type typeLastPlayWire } from "./LastPlayCard";
import { LastFiveSection } from "./LastFiveSection";
import { LeadersSection } from "./LeadersSection";
import { LineScoreSection } from "./LineScoreSection";
import { PitchersSection } from "./PitchersSection";
import type { typeMatchupSection, typeMatchupTeamMark } from "./matchupTypes";
import {
  buildLineResult,
  buildLineSummary,
  NO_LINE_COPY,
  NO_SPREAD_OR_TOTAL_COPY,
  type typeEventOdds,
} from "./oddsLogic";
import { ScoringSummarySection } from "./ScoringSummarySection";
import { StatComparisonSection } from "./StatComparisonSection";
import { resolvePlatePair } from "./teamPlateText";

export type { typeMatchupSection } from "./matchupTypes";

type typeMatchupTabTeam = {
  teamId: string;
  name: string;
  abbreviation: string;
  logoUrl: string | null;
  shortName?: string | null;
};

const RowsSection = ({
  section,
}: {
  section: typeMatchupSection;
}): ReactNode => (
  <GameSectionCard testId="matchup-section" title={section.title}>
    {section.rows.length === 0 ? (
      <GameSectionEmpty />
    ) : (
      <div className="flex flex-col">
        {section.rows.map((row) => (
          <div
            key={row.key}
            className="flex min-h-touch-target items-center justify-between gap-2 border-t border-hairline text-body text-ink tabular-nums first:border-t-0"
          >
            <span>{row.awayValue}</span>
            <span className="text-small text-ink-secondary">{row.label}</span>
            <span>{row.homeValue}</span>
          </div>
        ))}
      </div>
    )}
  </GameSectionCard>
);

const renderSection = ({
  section,
  away,
  home,
  isScoringSummaryInSideColumn,
  isLive,
}: {
  section: typeMatchupSection;
  away: typeMatchupTeamMark;
  home: typeMatchupTeamMark;
  isScoringSummaryInSideColumn: boolean;
  isLive: boolean;
}): ReactNode => {
  switch (section.kind ?? "rows") {
    case "rows":
      return <RowsSection key={section.key} section={section} />;
    case "lineScore":
      return section.lineScore ? (
        <LineScoreSection
          key={section.key}
          title={section.title}
          lineScore={section.lineScore}
          away={away}
          home={home}
          isLive={isLive}
        />
      ) : null;
    case "scoringSummary":
      return section.scoringSummary &&
        section.scoringSummary.entries.length > 0 ? (
        <ScoringSummarySection
          key={section.key}
          title={section.title}
          entries={section.scoringSummary.entries}
          isHiddenAtLg={isScoringSummaryInSideColumn}
        />
      ) : null;
    case "leaders":
      return section.leaders ? (
        <LeadersSection
          key={section.key}
          title={section.title}
          categories={section.leaders.categories}
          away={away}
          home={home}
        />
      ) : null;
    case "statComparison":
      return section.statComparison ? (
        <StatComparisonSection
          key={section.key}
          title={section.title}
          rows={section.statComparison.rows}
          away={away}
          home={home}
        />
      ) : null;
    case "details":
      return section.details ? (
        <DetailsSection
          key={section.key}
          title={section.title}
          rows={section.details.rows}
        />
      ) : null;
    case "headToHead":
      return section.headToHead ? (
        <HeadToHeadSection
          key={section.key}
          title={section.title}
          meetings={section.headToHead.meetings}
          away={away}
          home={home}
        />
      ) : null;
    case "lastFive":
      return section.lastFive ? (
        <LastFiveSection
          key={section.key}
          title={section.title}
          lastFive={section.lastFive}
          away={away}
          home={home}
        />
      ) : null;
    case "pitchers":
      return section.pitchers && section.pitchers.rows.length > 0 ? (
        <PitchersSection
          key={section.key}
          title={section.title}
          pitchers={section.pitchers}
          away={away}
          home={home}
        />
      ) : null;
    default:
      // Any other kind renders nothing, so the server can ship a new kind
      // before this client does.
      return null;
  }
};

const BettingLine = ({
  summary,
  result,
}: {
  summary: string;
  result: string | null;
}): ReactNode => {
  const isEmpty =
    summary === NO_LINE_COPY || summary === NO_SPREAD_OR_TOTAL_COPY;
  return (
    <GameSectionCard testId="betting-line" title="Betting line">
      <p
        className={
          isEmpty ? "text-body text-ink-secondary" : "text-body text-ink"
        }
        data-testid="betting-line-summary"
      >
        {summary}
      </p>
      {result === null ? null : (
        <p
          className="text-body-strong text-ink"
          data-testid="betting-line-result"
        >
          {result}
        </p>
      )}
    </GameSectionCard>
  );
};

export const MatchupTab = ({
  sections,
  odds,
  away,
  home,
  isWide,
  isScoringSummaryInSideColumn = false,
  liveState = null,
  lastPlay = null,
  isLive = false,
  isOddsOffered = true,
}: {
  sections: readonly typeMatchupSection[];
  odds: typeEventOdds;
  away: typeMatchupTabTeam;
  home: typeMatchupTabTeam;
  isWide: boolean;
  /** ISSUE-989. True when the desktop column already renders the scoring summary at lg. */
  isScoringSummaryInSideColumn?: boolean;
  /** ISSUE-1091. The server's live state; null when the game is not in progress. */
  liveState?: typeLiveStateWire | null;
  /** ISSUE-1382. The server's newest play; null unless the game is in progress. */
  lastPlay?: typeLastPlayWire | null;
  /** The game is in progress: the line score tints its current period. */
  isLive?: boolean;
  /**
   * False when the server did not offer the Odds tab for this game, which is
   * a league that carries no lines (EPL and MLS: GoalServe serves no odds for
   * them and the owner waived the feed for launch, 2026-10-29). With no line
   * stored either, the Betting line card is not drawn at all instead of
   * saying "No line yet" for a league that will never have one.
   */
  isOddsOffered?: boolean;
}): ReactNode => {
  const { awayText, homeText } = resolvePlatePair({
    away: { name: away.name, abbreviation: away.abbreviation },
    home: { name: home.name, abbreviation: home.abbreviation },
  });
  const summary = buildLineSummary({
    odds,
    homeTeamId: home.teamId,
    homeText,
    awayTeamId: away.teamId,
    awayText,
  });
  const result = buildLineResult({ odds, homeText, awayText });
  const bettingLine =
    isOddsOffered || odds !== null ? (
      <BettingLine summary={summary} result={result} />
    ) : null;
  const awayMark: typeMatchupTeamMark = {
    name: away.name,
    plateText: awayText,
    logoUrl: away.logoUrl,
    teamId: away.teamId,
    abbreviation: away.abbreviation,
    shortName: away.shortName ?? null,
  };
  const homeMark: typeMatchupTeamMark = {
    name: home.name,
    plateText: homeText,
    logoUrl: home.logoUrl,
    teamId: home.teamId,
    abbreviation: home.abbreviation,
    shortName: home.shortName ?? null,
  };
  const livePanel =
    liveState === null ? null : (
      <LiveStatePanel
        liveState={liveState}
        awayPlateText={awayText}
        homePlateText={homeText}
      />
    );
  const lastPlayCard =
    lastPlay === null ? null : (
      <LastPlayCard
        play={lastPlay}
        away={{
          teamId: away.teamId,
          abbreviation: away.abbreviation,
          plateText: awayText,
          logoUrl: away.logoUrl,
        }}
        home={{
          teamId: home.teamId,
          abbreviation: home.abbreviation,
          plateText: homeText,
          logoUrl: home.logoUrl,
        }}
      />
    );
  const render = (section: typeMatchupSection): ReactNode =>
    renderSection({
      section,
      away: awayMark,
      home: homeMark,
      isScoringSummaryInSideColumn,
      isLive,
    });
  // Spec 7.6's order: the live gamecast, the line score, the odds, then the
  // rest in the server's order. Only the line score moves ahead of the odds.
  const lineScores = sections.filter((section) => section.kind === "lineScore");
  const rest = sections.filter((section) => section.kind !== "lineScore");

  if (!isWide) {
    return (
      <div data-testid="matchup-tab" className="flex flex-col gap-4">
        {livePanel}
        {lastPlayCard}
        {lineScores.map(render)}
        {bettingLine}
        {rest.map(render)}
      </div>
    );
  }

  // The primary column is always a prefix of the server's list, so the order
  // below lg (one stacked column) is the server's order.
  const splitIndex =
    rest.findIndex((section) => section.kind === "leaders") + 1;
  return (
    <div
      data-testid="matchup-tab"
      className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6"
    >
      <div
        data-testid="matchup-column-primary"
        className="flex min-w-0 flex-col gap-4 lg:flex-1"
      >
        {livePanel}
        {lastPlayCard}
        {lineScores.map(render)}
        {bettingLine}
        {rest.slice(0, splitIndex).map(render)}
      </div>
      <div
        data-testid="matchup-column-secondary"
        className="flex min-w-0 flex-col gap-4 lg:flex-1"
      >
        {rest.slice(splitIndex).map(render)}
      </div>
    </div>
  );
};
